import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useTheme } from '@/contexts/ThemeContext';
import { Menu, X, BookOpen, Swords } from 'lucide-react';

export const Navigation = () => {
  const location = useLocation();
  const { theme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const logoSrc = theme === 'light' ? '/logo-light' : '/logo-dark';

  const navLinks = [
    { to: '/', label: 'Home' },
    { to: '/compare', label: 'Compare' },
    { to: '/duel', label: '1 vs 1 Duel', icon: Swords },
    { to: '/trading-guide', label: 'Trading Guide' },
    { to: '/blog', label: 'News & Blog' },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  
  return (
    <>
      <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl shadow-sm">
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 to-background/70 pointer-events-none" />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center group" onClick={() => setMobileMenuOpen(false)}>
              <picture>
                <source srcSet={`${logoSrc}.webp`} type="image/webp" />
                <img 
                  src={`${logoSrc}.png`}
                  alt="NeoBroker Logo" 
                  width="160"
                  height="32"
                  fetchPriority="high"
                  decoding="async"
                  className="h-7 sm:h-8 w-auto transition-transform group-hover:scale-105"
                />
              </picture>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navLinks.map((link) => (
                <Link key={link.to} to={link.to}>
                  <Button 
                    variant={isActive(link.to) ? 'secondary' : 'ghost'}
                    className="font-medium"
                  >
                    {link.label}
                  </Button>
                </Link>
              ))}
            </div>
            
            {/* Right Side Actions */}
            <div className="flex items-center space-x-2">
              <ThemeToggle />
              
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6 text-foreground" />
                ) : (
                  <Menu className="w-6 h-6 text-foreground" />
                )}
              </button>

              <Button 
                size="sm"
                className="hidden sm:flex bg-gradient-to-r from-[hsl(210,100%,56%)] to-[hsl(190,95%,55%)] hover:opacity-90 transition-opacity shadow-md text-xs sm:text-sm px-3 sm:px-4"
                data-testid="get-started-btn"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
        
        {/* Subtle bottom border */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-border/50" />

        {/* Mobile Menu Dropdown */}
        <div 
          className={`md:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-xl border-b border-white/10 shadow-2xl transition-all duration-300 ease-out overflow-hidden ${
            mobileMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
          }`}
          data-testid="mobile-menu"
        >
          <div className="container mx-auto px-4 py-4 space-y-2">
            {navLinks.map((link) => (
              <Link 
                key={link.to} 
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
              >
                <div 
                  className={`w-full px-4 py-3 rounded-xl text-left font-medium transition-all ${
                    isActive(link.to)
                      ? 'bg-gradient-to-r from-[hsl(210,100%,56%)]/20 to-[hsl(190,95%,55%)]/20 text-foreground border border-white/20'
                      : 'text-foreground/70 hover:text-foreground hover:bg-white/5'
                  }`}
                  data-testid={`mobile-nav-${link.label.toLowerCase().replaceAll(' ', '-')}`}
                >
                  {link.label}
                </div>
              </Link>
            ))}
            
            <div className="pt-4 border-t border-white/10">
              <Button 
                className="w-full bg-gradient-to-r from-[hsl(210,100%,56%)] to-[hsl(190,95%,55%)] hover:opacity-90 transition-opacity shadow-lg"
                onClick={() => setMobileMenuOpen(false)}
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};
